/** Typed client for the mediator API. The JWT lives in localStorage and is attached to every call. */

const TOKEN_KEY = 'ssemr.mediator.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The parsed response body, when there was one — e.g. the bulk-import endpoint's `problems` array. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised on 401 so the shell can drop the session rather than showing a generic failure. */
export class SessionExpiredError extends ApiError {}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const body = text ? safeParse(text) : undefined;

  if (!response.ok) {
    const message = (body as { error?: string })?.error ?? `Request failed (HTTP ${response.status})`;
    if (response.status === 401) {
      throw new SessionExpiredError(message, 401, body);
    }
    throw new ApiError(message, response.status, body);
  }

  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: (path: string) => request<void>(path, { method: 'DELETE' }),
};

/* ---------- shapes mirrored from the server's domain types ---------- */

export type UserRole = 'viewer' | 'operator' | 'admin' | 'superadmin';

/** Capabilities, mirrored from the server's permission matrix. */
export type Permission =
  | 'submissions.read'  | 'submissions.retry'
  | 'queue.read'        | 'queue.manage'
  | 'facilities.read'   | 'facilities.write' | 'facilities.delete' | 'facilities.rotate_key'
  | 'targets.read'      | 'targets.test'     | 'targets.write'
  | 'mappings.read'     | 'mappings.write'
  | 'audit.read'        | 'audit.read_all'
  | 'users.read'        | 'users.write'
  | 'sessions.read'     | 'sessions.revoke';

export interface FacilityScope {
  all: boolean;
  facilityIds: number[];
}

/** What the signed-in user is, may do, and may see. */
export interface Session {
  user: User;
  permissions: Permission[];
  scope: FacilityScope;
  assignableRoles: UserRole[];
}

export const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Viewer',
  operator: 'Operator',
  admin: 'Administrator',
  superadmin: 'Super administrator',
};

export const ROLE_BLURBS: Record<UserRole, string> = {
  viewer: 'Reads submissions, queue, facilities and mappings within their facilities.',
  operator: 'Also retries submissions, manages queued work and tests DHIS2 servers.',
  admin: 'Also edits facilities and mappings. Cannot manage users or DHIS2 credentials.',
  superadmin: 'Everything, across every facility: users, DHIS2 credentials, deletions, full activity log.',
};

export interface User {
  id: number;
  /** Generated at creation. An account without one cannot sign in. */
  identifier: string | null;
  email: string;
  name: string;
  role: UserRole;
  disabled: boolean;
  scopeAll: boolean;
  facilityIds?: number[];
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Dhis2Target {
  id: number;
  name: string;
  baseUrl: string;
  authType: 'token' | 'basic' | 'none';
  username: string | null;
  hasSecret: boolean;
  importStrategy: string;
  dryRun: boolean;
  enabled: boolean;
  isDefault: boolean;
}

export interface Facility {
  id: number;
  code: string;
  name: string;
  dhis2OrgUnit: string | null;
  targetId: number | null;
  targetName: string | null;
  apiKeyHint: string | null;
  enabled: boolean;
  lastSeenAt: string | null;
}

export type MappingKind = 'dataset' | 'element' | 'combo';

export interface Mapping {
  id: number;
  kind: MappingKind;
  sourceCode: string;
  dhis2Uid: string;
  name: string | null;
  facilityId: number | null;
  facilityCode: string | null;
  enabled: boolean;
}

export type SubmissionStatus =
  | 'received'
  | 'queued'
  | 'unmapped'
  | 'sent'
  | 'partial'
  | 'failed';

export interface Submission {
  id: number;
  facilityId: number;
  facilityCode: string;
  facilityName: string;
  reportId: string;
  sourceDataset: string;
  period: string;
  orgUnit: string | null;
  targetName: string | null;
  status: SubmissionStatus;
  attempts: number;
  valueCount: number;
  warningCount: number;
  error: string | null;
  receivedAt: string;
  lastAttemptAt: string | null;
}

export interface SubmissionDetail extends Submission {
  measureReport: unknown;
  dataValueSet: unknown;
  dhis2Response: unknown;
  warnings: Array<{ type: string; kind?: string; sourceCode?: string; message: string }>;
  fhirLocation: string | null;
  attempts_?: never;
  attempts: number;
}

export interface Overview {
  queue: QueueStats;
  audit: AuditStats;
  stats: {
    total: number;
    byStatus: Record<string, number>;
    facilitiesReporting: number;
    last24h: number;
    latestPeriod: string | null;
  };
  volume: Array<{ day: string; sent: number; failed: number; other: number }>;
  unmappedCodes: Array<{ kind: MappingKind; sourceCode: string; occurrences: number }>;
  facilities: { total: number; enabled: number; missingOrgUnit: number };
  targets: { total: number; enabled: number; dryRun: number };
}

/* ---------- queue ---------- */

export type QueueName = 'inbound' | 'outbound';
export type JobStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'dead' | 'cancelled';

export interface QueueJob {
  id: number;
  queue: QueueName;
  submissionId: number;
  status: JobStatus;
  priority: number;
  attempts: number;
  maxAttempts: number;
  runAfter: string;
  lockedBy: string | null;
  lastError: string | null;
  triggeredBy: string;
  createdAt: string;
  updatedAt: string;
  facilityCode: string;
  facilityName: string;
  period: string;
  reportId: string;
  submissionStatus: SubmissionStatus;
  valueCount: number;
}

export interface QueueDepth {
  pending: number;
  running: number;
  dead: number;
  succeeded: number;
  oldestPendingAt: string | null;
}

export type QueueStats = Record<QueueName, QueueDepth>;

/* ---------- audit ---------- */

export type AuditCategory = 'auth' | 'user' | 'config' | 'data' | 'system';

export interface AuditRow {
  id: number;
  occurredAt: string;
  category: AuditCategory;
  action: string;
  outcome: 'success' | 'failure';
  actorType: 'user' | 'facility' | 'system';
  actorId: number | null;
  actorLabel: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  summary: string;
  detail: unknown;
}

export interface AuditStats {
  total: number;
  last24h: number;
  failures24h: number;
  distinctIps7d: number;
  byCategory: Record<string, number>;
}

/* ---------- sessions ---------- */

export interface ActiveSession {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  ipAddress: string | null;
  lastIp: string | null;
  userAgent: string | null;
  deviceLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}
